import React, { useState, useEffect } from 'react';
import './HeroBanner.css';
import { getMultipleImageSets, getCachedImages, cacheImages, getDefaultImages } from '../../services/unsplashService';

const HeroBanner = () => {
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  // 加载图片
  useEffect(() => {
    const loadImages = async () => {
      try {
        setLoading(true);
        
        // 先尝试从缓存获取
        let imageData = getCachedImages();
        
        // 如果缓存不存在或过期，从API获取
        if (!imageData || imageData.length === 0) {
          console.log('从Unsplash API获取图片...');
          imageData = await getMultipleImageSets(6);
          // 缓存图片
          if (imageData && imageData.length > 0) {
            cacheImages(imageData);
            console.log('✅ 图片已缓存，数量:', imageData.length);
          } else {
            console.warn('API获取图片失败，使用默认图片');
            imageData = getDefaultImages();
          }
        } else {
          console.log('✅ 使用缓存图片，数量:', imageData.length);
        }
        
        // 如果API获取失败，使用默认图片
        if (!imageData || imageData.length === 0) {
          console.warn('使用默认图片');
          imageData = getDefaultImages();
        }
        
        setImages(imageData);
      } catch (error) {
        console.error('加载图片失败:', error);
        // 使用默认图片
        setImages(getDefaultImages());
      } finally {
        setLoading(false);
      }
    };

    loadImages();
  }, []);

  // 图片轮播
  useEffect(() => {
    if (images.length === 0) return;
    
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 4000); // 每4秒切换一次

    return () => clearInterval(timer);
  }, [images.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  return (
    <div className="hero-banner">
      <div className="hero-image-container">
        <div className="carousel-wrapper">
          <div
            className="carousel-slides"
            style={{
              transform: `translateX(-${currentIndex * 100}%)`,
            }}
          >
            {images.map((image, index) => {
              // 为每个slide生成不同的渐变背景作为备用
              const gradients = [
                'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
                'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
              ];
              
              // 处理图片对象或字符串URL
              const imageUrl = typeof image === 'string' ? image : (image.url || image.fullUrl);
              const imageId = typeof image === 'string' ? `image-${index}` : (image.id || imageUrl || `image-${index}`);
              const imageAlt = typeof image === 'string' 
                ? `Crypto banner ${index + 1}` 
                : (image.description || `Crypto banner ${index + 1}`);
              
              return (
                <div 
                  key={`${imageId}-${index}`} 
                  className="carousel-slide"
                  style={{ background: gradients[index % gradients.length] }}
                >
                  {loading && index === 0 ? (
                    <div className="carousel-loading">
                      <div className="loading-spinner"></div>
                      <p>加载中...</p>
                    </div>
                  ) : (
                    <img
                      src={imageUrl}
                      alt={imageAlt}
                      className="carousel-image"
                      loading={index === 0 ? 'eager' : 'lazy'}
                      onError={(e) => {
                        // 防止无限循环：只尝试一次备用URL
                        const img = e.target;
                        const imageObj = typeof image === 'object' ? image : null;
                        
                        // 检查是否已经尝试过备用URL（通过data属性标记）
                        const hasTriedFallback = img.dataset.triedFallback === 'true';
                        
                        if (!hasTriedFallback && imageObj) {
                          // 标记已尝试
                          img.dataset.triedFallback = 'true';
                          
                          // 如果有fullUrl且当前URL不是fullUrl，尝试使用fullUrl
                          if (imageObj.fullUrl && img.src !== imageObj.fullUrl) {
                            img.src = imageObj.fullUrl;
                            return;
                          }
                          
                          // 如果有thumbUrl，尝试使用
                          if (imageObj.thumbUrl && img.src !== imageObj.thumbUrl) {
                            img.src = imageObj.thumbUrl;
                            return;
                          }
                        }
                        
                        // 如果所有URL都失败，隐藏图片，使用渐变背景
                        img.style.display = 'none';
                        img.style.opacity = '0';
                      }}
                    />
                  )}
                  <div className="hero-overlay">
                    <div className="bitcoin-logo">₿</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* 左右箭头 */}
          <button className="carousel-button carousel-button-prev" onClick={goToPrevious}>
            ‹
          </button>
          <button className="carousel-button carousel-button-next" onClick={goToNext}>
            ›
          </button>

          {/* 指示器 */}
          <div className="carousel-indicators">
            {images.map((_, index) => (
              <button
                key={index}
                className={`carousel-indicator ${
                  index === currentIndex ? 'active' : ''
                }`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroBanner;

